import { cn } from '@/lib/utils';
import { ReloadIcon } from '@radix-ui/react-icons';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

const buttonVariants = cva(
  'inline-flex items-center w-[130px] justify-center whitespace-nowrap rounded-xl text-sm font-bold font-medium ring-offset-background transition-colors focus-visible:outline-none  disabled:pointer-events-none disabled:opacity-50 transition-all duration-400',
  {
    variants: {
      variant: {
        default:
          'text-textPrimary bg-gradient-to-br from-[#F8B107] from-[19.48%] to-[#EFE873] to-[86.25%] hover:from-[#c98c02] hover:to-[#bdb640]',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90 ring-0',
        outline:
          'border border-input border-[#8B92A1] bg-background hover:bg-accent hover:text-accent-foreground',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: '',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 rounded-xl px-3 font-bold',
        lg: 'h-11 rounded-xl px-8 font-bold',
        icon: 'h-10 w-10 rounded-full',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  id: string;
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, asChild = false, id, loading, disabled, type = 'button', ...props },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';
    const children = loading ? (
      <>
        <ReloadIcon className='mr-2 h-3 w-3 animate-spin' />
        {props.children}
      </>
    ) : (
      props.children
    );
    return (
      <Comp
        id={id}
        data-testid={`${id}-testid`}
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        type={type}
        {...props}
        disabled={loading || disabled}
        aria-disabled={loading || disabled}
      >
        {children}
      </Comp>
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
